/** The info plate at the foot of the command column.
 *
 * There is exactly one of these on the screen (see `STATUS_BAR_NOTE`), and it
 * answers, in priority order:
 *
 *   1. why the last thing you tried was refused,
 *   2. what the move you are proposing costs,
 *   3. what the pointer is over - which thing, and what visiting it would do,
 *   4. and failing all of those, the ground the selected hero is standing on.
 *
 * The fourth line is the reason it is never empty.
 *
 * It used to be Movement and Mana, printed as words. That made those two
 * numbers the third statement of themselves inside one 500px column - the
 * roster row had them as bars and the hero sheet has them as gauges - and it
 * left the plate a 260x80 slab with its right 40% blank.
 *
 * Then it was `Jorthas` over `Grass - (17, 40, level 1)`: the hero's name for the
 * third time in the same column, and under it the raw tile coordinates and the
 * engine's z index rendered as prose. A player has no use for either. The plate
 * now says what `describeHover` says - a name and a consequence - and the
 * resting state is that same description applied to the hero's own cell, so
 * the plate teaches its format before the pointer ever reaches the map.
 *
 * Coordinates are a developer's tool. `cellDebugText` formats them and this
 * component renders them only when `globalThis.__HM_DEBUG_COORDS__` is set,
 * which nothing in the app does - a console does. The plate a player sees has
 * no coordinates in it at any time.
 *
 * The wording of an illegality still comes from the kernel's own `reasonText`,
 * through `statusLine`; nothing here paraphrases a refusal.
 */

import { useMemo } from "react";
import type { HeroId } from "@game/core";
import { BitmapText, fitRomanStep, romanFaceForStep, useDevicePixelRatio } from "../theme/bitmapText.js";
import { cx, useElementSize } from "../theme/components.js";
import { useContent, useObservation } from "../hooks/useGameState.js";
import { cellDebugText, describeHover, statusLine, type HoverDescription } from "./describe.js";
import { findHero, objectAt, townAt } from "./select.js";
import type { HeroCapacities, StatusState } from "./types.js";

export interface StatusBarProps {
  readonly status: StatusState;
  /** Whose ground to describe when the pointer is over nothing. */
  readonly selectedHeroId?: HeroId | null | undefined;
  /** Accepted and unused since the plate stopped restating Movement and Mana.
   *  Kept on the prop type because the adventure shell hands the same
   *  `capacities` to every panel in the column and a panel that refused it
   *  would just move the conditional up one level. */
  readonly capacities?: HeroCapacities | undefined;
  readonly className?: string | undefined;
  /**
   * The desktop plate: the small roman face at one magnification, held to
   * `PLATE_FLOOR_PX` rather than to the window's cap floor. The window's floor
   * promoted "Grass" to the 15-row caption face at 1080p, which beside the
   * 11-row readings in the sidebar read as a headline, and the player asked
   * for it smaller. The phone HUD leaves this off: its plate is drawn scaled
   * and measured for a thumb.
   */
  readonly small?: boolean | undefined;
}

/**
 * The desktop plate's own floor, in device pixels of cap: the original game's
 * smallest face, which is the roman face at one device pixel per texel. Under
 * an 80% page the reading is promoted a face to clear it, as any other would.
 */
const PLATE_FLOOR_PX = 11;

/** A developer's opt-in, off in every build. Deliberately not the name CI
 *  greps `dist/` for; the real home for this is `__GAME_DEBUG__`, which lives
 *  in `apps/web` and is not this package's to extend. */
const debugCoords = (): boolean =>
  (globalThis as { __HM_DEBUG_COORDS__?: boolean }).__HM_DEBUG_COORDS__ === true;

const EMPTY: HoverDescription = { title: "", detail: [] };

/**
 * Slack held back from the measured width before a magnification is picked.
 *
 * `useElementSize` reports the CONTENT box, so the plate's padding is already
 * out of it; what this covers is the rounding on both sides of that box and
 * the plate's own two lips. A constant for the same reason `GAUGE_CHROME_PX`
 * is one - it is compared against a measured width only to pick a WHOLE
 * magnification, and no plausible slop here can move a whole step.
 */
const INFO_CHROME_PX = 4;

export function StatusBar(props: StatusBarProps): JSX.Element {
  const obs = useObservation();
  const content = useContent();
  const line = useMemo(() => statusLine(obs, content, props.status), [obs, content, props.status]);
  const hero = useMemo(
    () => findHero(obs, props.selectedHeroId ?? null),
    [obs, props.selectedHeroId],
  );

  // The resting state: what the selected hero is standing on, described the
  // same way the pointer would describe it. A hero in a town reads as the town,
  // a hero on a flagged mine reads as the mine, and a hero in a field reads
  // "Grass". Live - it changes with every step - and stated nowhere else.
  const resting = useMemo<HoverDescription>(() => {
    if (hero === null) return EMPTY;
    const town = townAt(obs, hero.x, hero.y, hero.z);
    if (town !== null) return describeHover(obs, content, { kind: "TOWN", id: town.id });
    const object = objectAt(obs, hero.x, hero.y, hero.z);
    if (object !== null) return describeHover(obs, content, { kind: "OBJECT", id: object.id });
    return describeHover(obs, content, { kind: "CELL", x: hero.x, y: hero.y, z: hero.z });
  }, [hero, obs, content]);

  const idle = line.text === "";
  const title = idle ? (resting.title === "" ? "No hero selected" : resting.title) : line.text;
  const lines = idle ? resting.detail : line.detail;
  const coords = hero !== null && debugCoords()
    ? cellDebugText(hero.x, hero.y, hero.z, obs.map.levels)
    : null;

  // The plate is stamped, and the whole plate steps together.
  //
  // A title in the kit's display serif over a detail line in its label serif
  // was two more type systems on a screen that already carried the bitmap face
  // everywhere else, and the plate is where the two sat closest - `Snow` over
  // `Slow going`, one glyph row apart. Both are cut from the same face now,
  // and the magnification is picked by measuring the WIDEST line rather than
  // by asking the window how wide it is: `Remembered, not currently in sight`
  // and `Grass` are the same plate on the same screen and no media query can
  // tell them apart.
  const { ref: boxRef, width } = useElementSize<HTMLDivElement>();
  const all = [title, ...lines, ...(coords === null ? [] : [coords])];
  // The plate's HEIGHT is the resource bar's, so it is fixed, and three lines
  // at magnification 2 are 66 device px in a 51px content box - the third one
  // would be clipped. Three lines is what an object with an effect, an owner
  // and a "remembered" note produces, so it is not a corner case. A stack that
  // deep steps down instead. Title and lines are one family now (`roman`),
  // so one measurement answers for the plate - as DRAWN, the floor applied.
  const room = width - INFO_CHROME_PX;
  const dpr = useDevicePixelRatio();
  // One step for the whole plate, in the face it is stamped in: the roman
  // family carries its sizes as faces rather than as magnifications, so the
  // step is 2 (`romanDisplay`) only where every line of the plate fits it.
  const small = props.small === true;
  const scale = small || all.length >= 3 ? 1 : fitRomanStep(room, all, dpr);
  const face = romanFaceForStep(scale);
  const floor = small ? PLATE_FLOOR_PX : true;

  return (
    <div
      ref={boxRef}
      className={cx("hm-adv-info", line.illegal && "hm-adv-info--illegal", props.className)}
      role="status"
      aria-live="polite"
    >
      {line.code !== null && (
        <span className="hm-adv-info__badge">
          <BitmapText face="roman" scale={1} floor={floor}>{String(line.code)}</BitmapText>
        </span>
      )}
      <span className="hm-adv-info__title">
        <BitmapText face={face} scale={1} floor={floor}>{title}</BitmapText>
      </span>
      {lines.map((text, i) => (
        <span
          key={i}
          className={cx("hm-adv-info__line", idle && "hm-adv-info__line--strong")}
        >
          <BitmapText face={face} scale={1} floor={floor}>{text}</BitmapText>
        </span>
      ))}
      {coords !== null && (
        <span className="hm-adv-info__line">
          <BitmapText face={face} scale={1} floor={floor}>{coords}</BitmapText>
        </span>
      )}
    </div>
  );
}
